import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Loader2,
  Plus,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  Palette,
  Sparkles,
  Layout,
  Search,
  Check,
  Aperture,
} from 'lucide-react';
import { TemplateBuilder } from './TemplateBuilder';
import { ApplyTemplateModal } from './ApplyTemplateModal';
import { toast } from 'sonner';

export interface TemplateSettings {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  fonts: {
    title: string;
    subtitle: string;
    body: string;
    titleSize: number;
    subtitleSize: number;
    bodySize: number;
  };
  titles: {
    position: string;
    style: string;
    shadow: boolean;
    shadowColor: string;
    animation: string;
  };
  lowerThirds: {
    enabled: boolean;
    position: string;
    style: string;
    backgroundColor: string;
  };
  logo: {
    enabled: boolean;
    url: string;
    s3Key: string;
    name: string;
    position: string;
    size: string;
    opacity: number;
  };
  overlays: {
    enabled: boolean;
    type: string;
    color: string;
    opacity: number;
  };
  filters: {
    enabled: boolean;
    type: string;
    intensity: number;
  };
  background: {
    type: string;
    color: string;
    url: string;
    blur: number;
  };
  audio: {
    musicTrackUrl: string;
    musicTrackS3Key: string;
    musicTrackName: string;
    musicVolume: number;
    voiceVolume: number;
    fadeIn: boolean;
    fadeOut: boolean;
    normalize: boolean;
  };
  timeline: {
    introClipUrl: string;
    introClipS3Key: string;
    introClipName: string;
    introDuration: number;
    outroClipUrl: string;
    outroClipS3Key: string;
    outroClipName: string;
    outroDuration: number;
    defaultTransition: string;
    transitionDuration: number;
    defaultClipDuration: number;
    defaultCaptionDuration: number;
  };
  aiDefaults: {
    captionStyle: string;
    captionFont: string;
    captionSize: number;
    captionColor: string;
    captionBackground: string;
    captionPosition: string;
    safeArea: boolean;
    aspectRatio: string;
    resolution: string;
    promptPatterns: string[];
  };
  social: {
    defaultCaption: string;
    defaultHashtags: string[];
    defaultTitle: string;
  };
}

export interface VideoTemplate {
  _id: string;
  userId?: string;
  name: string;
  description: string;
  settings: TemplateSettings;
  thumbnailUrl?: string;
  isPreset?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ProjectFile {
  id: string;
  name: string;
  fileType?: 'video' | 'audio' | 'image';
  thumbnailUrl?: string;
  s3Key?: string;
  s3Url?: string;
  url?: string;
  duration?: number;
}

interface TemplatesTabProps {
  onApplyTemplate: (template: VideoTemplate) => void;
  currentProjectSettings?: Partial<TemplateSettings>;
  projectFiles?: ProjectFile[];
}

export const TemplatesTab: React.FC<TemplatesTabProps> = ({
  onApplyTemplate,
  currentProjectSettings,
  projectFiles = [],
}) => {
  const [templates, setTemplates] = useState<VideoTemplate[]>([]);
  const [presets, setPresets] = useState<VideoTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBuilder, setShowBuilder] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<VideoTemplate | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<VideoTemplate | null>(null);

  useEffect(() => {
    fetchTemplates();
    fetchPresets();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch('/api/templates', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPresets = async () => {
    try {
      const response = await fetch('/api/templates/presets');
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets || []);
      }
    } catch (error) {
      console.error('Failed to fetch presets:', error);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setShowBuilder(true);
  };

  const handleEditTemplate = (template: VideoTemplate) => {
    setEditingTemplate(template);
    setShowBuilder(true);
  };

  const handleDuplicateTemplate = async (template: VideoTemplate) => {
    try {
      const response = await fetch(`/api/templates/${template._id}/duplicate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Template duplicated');
        fetchTemplates();
      } else {
        toast.error('Failed to duplicate template');
      }
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  const handleDeleteTemplate = async (template: VideoTemplate) => {
    if (!confirm(`Delete "${template.name}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(`/api/templates/${template._id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleCreateFromPreset = async (preset: VideoTemplate) => {
    try {
      const response = await fetch(`/api/templates/from-preset/${preset._id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: `My ${preset.name} Template` }),
      });
      if (response.ok) {
        toast.success('Template created from preset');
        fetchTemplates();
      } else {
        toast.error('Failed to create template');
      }
    } catch (error) {
      console.error('Failed to create template:', error);
      toast.error('Failed to create template');
    }
  };

  const handleApplyTemplate = (template: VideoTemplate) => {
    setSelectedTemplate(template);
    setShowApplyModal(true);
  };

  const handleConfirmApply = () => {
    if (selectedTemplate) {
      onApplyTemplate(selectedTemplate);
      setShowApplyModal(false);
      toast.success(`Applied "${selectedTemplate.name}" template`);
    }
  };

  const handleSaveTemplate = async (template: Partial<VideoTemplate>) => {
    try {
      const isEditing = editingTemplate && editingTemplate._id;
      const url = isEditing ? `/api/templates/${editingTemplate._id}` : '/api/templates';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(template),
      });

      if (response.ok) {
        toast.success(isEditing ? 'Template updated' : 'Template created');
        fetchTemplates();
        setShowBuilder(false);
        setEditingTemplate(null);
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleSaveCurrentAsTemplate = () => {
    if (currentProjectSettings) {
      setEditingTemplate({
        _id: '',
        name: 'New Template',
        description: '',
        settings: currentProjectSettings as TemplateSettings,
      });
      setShowBuilder(true);
    }
  };

  const filteredTemplates = templates.filter(
    (t) =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getPresetIcon = (presetId: string) => {
    switch (presetId) {
      case 'preset-minimal':
        return <Layout className="h-5 w-5" />;
      case 'preset-bold':
        return <Sparkles className="h-5 w-5" />;
      case 'preset-cinematic':
        return <Aperture className="h-5 w-5" />;
      case 'preset-social':
        return <Palette className="h-5 w-5" />;
      default:
        return <Layout className="h-5 w-5" />;
    }
  };

  const getPresetColor = (presetId: string) => {
    switch (presetId) {
      case 'preset-minimal':
        return 'bg-gray-100 text-gray-700';
      case 'preset-bold':
        return 'bg-gradient-to-r from-red-500 to-yellow-500 text-white';
      case 'preset-cinematic':
        return 'bg-amber-100 text-amber-800';
      case 'preset-social':
        return 'bg-pink-100 text-pink-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex gap-2">
        <Button onClick={handleCreateTemplate} size="sm" className="flex-1">
          <Plus className="h-4 w-4 mr-1" />
          New Template
        </Button>
        {currentProjectSettings && (
          <Button onClick={handleSaveCurrentAsTemplate} size="sm" variant="outline">
            Save Current
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8"
        />
      </div>

      <ScrollArea className="h-[400px]">
        <div className="space-y-4">
          {/* Preset Templates */}
          {!searchQuery && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Start from Preset
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {presets.map((preset) => (
                  <Card
                    key={preset._id}
                    className="p-3 cursor-pointer hover:ring-1 hover:ring-primary transition-all"
                    onClick={() => handleApplyTemplate(preset)}
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className={`p-2 rounded-md ${getPresetColor(preset._id)}`}
                      >
                        {getPresetIcon(preset._id)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{preset.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {preset.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="flex-1 h-7 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApplyTemplate(preset);
                        }}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Apply
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateFromPreset(preset);
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* User Templates */}
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              My Templates ({filteredTemplates.length})
            </h4>
            {filteredTemplates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                {searchQuery ? 'No templates match your search' : 'No custom templates yet'}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTemplates.map((template) => (
                  <Card
                    key={template._id}
                    className="p-3 cursor-pointer hover:ring-1 hover:ring-primary transition-all"
                    onClick={() => handleApplyTemplate(template)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <div
                          className="w-10 h-10 rounded flex-shrink-0 flex items-center justify-center"
                          style={{
                            backgroundColor:
                              template.settings?.colors?.background || '#000',
                          }}
                        >
                          <span
                            className="text-lg font-bold"
                            style={{
                              color: template.settings?.colors?.primary || '#fff',
                            }}
                          >
                            {template.name[0]?.toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{template.name}</p>
                          {template.description && (
                            <p className="text-xs text-muted-foreground truncate">
                              {template.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{
                                backgroundColor:
                                  template.settings?.colors?.primary || '#fff',
                              }}
                            />
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{
                                backgroundColor:
                                  template.settings?.colors?.accent || '#3b82f6',
                              }}
                            />
                            <span className="text-xs text-muted-foreground">
                              {template.settings?.fonts?.title || 'Open Sans'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleApplyTemplate(template)}>
                            <Check className="h-4 w-4 mr-2" />
                            Apply
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditTemplate(template)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicateTemplate(template)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteTemplate(template)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </ScrollArea>

      {/* Template Builder Modal */}
      {showBuilder && (
        <TemplateBuilder
          isOpen={showBuilder}
          onClose={() => {
            setShowBuilder(false);
            setEditingTemplate(null);
          }}
          onSave={handleSaveTemplate}
          template={editingTemplate}
          currentProjectSettings={currentProjectSettings}
          projectFiles={projectFiles}
        />
      )}

      {/* Apply Template Confirmation Modal */}
      {showApplyModal && selectedTemplate && (
        <ApplyTemplateModal
          isOpen={showApplyModal}
          onClose={() => {
            setShowApplyModal(false);
            setSelectedTemplate(null);
          }}
          onConfirm={handleConfirmApply}
          template={selectedTemplate}
        />
      )}
    </div>
  );
};
